/**
 * What happens when the MASTER SOLUTION step finishes — all of it, in one file.
 *
 * THREE PRODUCERS SHARE THIS: `msExtraction` (a teacher's upload), `msGeneration` (one job per root, filling gaps)
 * and `msRefine` (a correction). That is why these rules live here rather than next to a job type, as the
 * single-producer steps' do — three types cannot each own the same declaration, and the last of them to finish is
 * whichever one happens to be last. Each of the three sets `onStepComplete: masterSolutionStep`, and
 * `validateStepCompletionWiring` checks at boot that they all point at THIS object rather than three lookalikes.
 */

import { logger } from '@adornis/base/logging.js';
import { hasAnyGradingSchemeContent } from '../../../utils/solution-completeness.js';
import {
  mainTaskNumbersForIds,
  mainTasksForIds,
  producerOf,
  stepIsCompleted,
  type FinishedRun,
  type StepCompletion,
} from '../step.js';

/**
 * REFINE the scheme, not regenerate it. The scheme for task 3 exists; it is simply written against the old
 * solution. `gsGeneration` would decline it — `rootNeedsGsWork` only picks up roots that have no scheme at all —
 * so a generation would quietly do nothing and the stale scheme would survive.
 */
async function refineTheSchemeToMatch(run: FinishedRun): Promise<boolean> {
  const { examId, examState, changedTasks } = run;

  // Nothing CHANGED, which covers the two cases that must not chain and is the whole reason the diff exists.
  // Either the upload re-stated what was already there, or this run DELIVERED the artifact for the first time —
  // `persistTaskArtifact` reports a fill as neither, because there is no superseded version for anything
  // downstream to bring into line. The first extraction of an exam lands here, which is what lets it announce: a
  // dispatch below returns before the announcement, and the master-solution announcement is what the grading
  // scheme's start hangs on.
  if (changedTasks.length === 0) return false;

  const taskRefs = await mainTaskNumbersForIds(examId, changedTasks);
  if (taskRefs.length === 0) return false;

  // PER ROOT, because the question is per root. This used to ask `hasAnyGradingSchemeContent` about the WHOLE
  // EXAM and then refine every changed root — an exam-wide ANY authorising a per-root action, and the two
  // disagree whenever coverage is partial. `gsGeneration` is gated per root on its own master solution, so on a
  // re-upload root 1's scheme can exist while roots 2-4 are still waiting for theirs; one root having a scheme
  // then licensed a refine of all four, three of which had nothing to align.
  //
  // Roots with no scheme are skipped rather than refined: whichever GS run eventually produces one covers them.
  const rootsWithScheme = (await mainTasksForIds(examId, changedTasks)).filter(main =>
    hasAnyGradingSchemeContent([main.node]),
  );
  if (rootsWithScheme.length === 0) return false;

  const schemeRefs = rootsWithScheme.map(main => main.taskNumber);

  // THE INTENT BELONGS HERE, not with whoever triggered the master-solution change, and that is the whole reason
  // this hop carries one. `refine_master_solution` used to dispatch this same refine itself, after its wait,
  // purely to attach `cause: 'master_solution_update'` — and lost the race: this closure fires from the msRefine
  // row's terminal write while the tool is still polling for it, so the tool's insert deduped against this one
  // (`gsRefine:<exam>:<target>:<refs>` does not include the intent, by design) and the cause went with it. The
  // re-grade then still ran, from the grading-scheme step's generic fallback, announcing a GRADING-SCHEME change
  // for an edit the teacher made to the master solution.
  //
  // Stating it here fixes that for every trigger at once rather than for the one that had a caller: an uploaded
  // master solution and a gap-filling generation reach this line too, and neither has a tool to attach anything.
  const regrade = examState?.hasGradingStarted ? { regrade: { cause: 'master_solution_update' as const } } : {};

  // The same partial-run dispatch the refine tools use — see `RefineRequest` in `refine.ts`.
  const { dispatchJobs } = await import('../dispatch.js');
  const { REFINE_JOB_TYPE } = await import('../job.js');
  void dispatchJobs(REFINE_JOB_TYPE.gradingScheme, examId, {
    taskRefs: schemeRefs,
    // No claim about HOW it was updated: this hop fires for any re-run of an already-delivered solution, an
    // upload and a gap-filling generation alike, and telling the agent it was an upload when it was not is a
    // detail it can repeat back to the teacher.
    feedback:
      'Die Musterlösung wurde soeben aktualisiert. Passe das Bewertungsschema automatisch an die neue Musterlösung an.',
    ...regrade,
  }).catch(err => logger.error({ err, examId, taskRefs }, 'Failed to queue grading-scheme refine'));

  logger.info(
    { examId, changedRefs: taskRefs, refinedRefs: schemeRefs },
    'Master solution changed — queued a grading-scheme refine for the changed roots that have a scheme',
  );
  return true;
}

export const masterSolutionStep: StepCompletion = {
  progress: {
    message: 'progress.masterSolutionReady',
    errorMessage: 'progress.errorExtractingMasterSolution',

    // An extraction is one pass over an upload and needs no gate, and neither does a refine — its tasks exist by
    // definition. A GENERATION is one job per root, so a drained queue does not mean no more roots will appear: the
    // structure extraction is what creates them.
    precondition: (examState, record) => producerOf(record) !== 'generation' || stepIsCompleted(examState, 'task'),

    /**
     * A REFINE ANNOUNCES NOTHING, and null is how a step says so — `updateSetupStep` creates no task when every field
     * of the delta is absent. A refine was asked for in chat and answered in the same turn, so the setup-step task
     * would be the teacher being told a second time, in the words of a delivery: "die Musterlösung wurde vollständig generiert" for a correction to one task. The follow-up still runs, so the re-grade a refine sets off
     * is unaffected.
     */
    delta: record => {
      const producer = producerOf(record);
      if (producer === 'refine') return null;
      return { masterSolution: { type: producer, reupload: record.reupload === true } };
    },
  },

  followUp: refineTheSchemeToMatch,
};
